// WelcomeScreen - Onboarding for first-time visitors

import React from 'react';
import { X, Coffee, BookOpen, TreePine, Users, Heart, MapPin, Sparkles } from 'lucide-react';

const THIRD_PLACE_EXAMPLES = [
  { icon: Coffee, label: 'Cafés & Bistros', description: 'Dein Stammcafé um die Ecke' },
  { icon: BookOpen, label: 'Bibliotheken', description: 'Ruhe und Wissen teilen' },
  { icon: TreePine, label: 'Parks & Gärten', description: 'Natur mitten in der Stadt' },
  { icon: Users, label: 'Community Spaces', description: 'Orte für Begegnung' },
];

const STORAGE_KEY = 'goodplace-welcome-seen';

interface WelcomeScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onExplore: () => void;
  onSuggest: () => void;
}

export function WelcomeScreen({ isOpen, onClose, onExplore, onSuggest }: WelcomeScreenProps) {
  if (!isOpen) return null;

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onClose();
  };

  const handleExplore = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onExplore();
  };

  const handleSuggest = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onSuggest();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition z-10"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-primary via-primary-dark to-secondary px-6 pt-8 pb-12 text-center text-white">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4">
            <MapPin className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Willkommen bei GoodPlace Match</h1>
          <p className="text-white/80 text-sm">
            Entdecke und teile "Dritte Orte" - Orte die dich glücklich machen
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6 -mt-6">
          {/* What are Third Places */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-indigo-500/10 rounded-lg">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 mb-1">Was sind "Dritte Orte"?</h2>
                <p className="text-sm text-gray-600">
                  Neben Zuhause (1. Ort) und Arbeit (2. Ort) gibt es besondere Plätze,
                  die zu unserem Wohlbefinden beitragen - unsere dritten Orte.
                  Sie sind Ankerpunkte im Alltag, wo wir entspannen, Menschen treffen
                  und einfach sein können.
                </p>
              </div>
            </div>
          </div>

          {/* Examples Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {THIRD_PLACE_EXAMPLES.map((example) => {
              const Icon = example.icon;
              return (
                <div
                  key={example.label}
                  className="p-3 bg-white border border-gray-200 rounded-xl hover:border-indigo-500/30 transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-900">{example.label}</span>
                  </div>
                  <p className="text-xs text-gray-500">{example.description}</p>
                </div>
              );
            })}
          </div>

          {/* Community note */}
          <div className="flex items-center gap-2 text-center justify-center mb-6">
            <Heart className="w-4 h-4 text-red-400" />
            <p className="text-sm text-gray-600">
              Von der Community für die Community
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleExplore}
              className="flex-1 py-3 px-4 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition"
            >
              Orte entdecken
            </button>
            <button
              onClick={handleSuggest}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition"
            >
              Ort vorschlagen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to check if welcome screen should be shown
export function useWelcomeScreen() {
  const [showWelcome, setShowWelcome] = React.useState(false);

  React.useEffect(() => {
    const hasSeenWelcome = localStorage.getItem(STORAGE_KEY);
    if (!hasSeenWelcome) {
      // Small delay to let the app load first
      const timer = setTimeout(() => setShowWelcome(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeWelcome = () => setShowWelcome(false);

  return { showWelcome, closeWelcome };
}

export default WelcomeScreen;
